/**
 * Native mobile features for NightBuZz (Capacitor)
 * Camera, Push Notifications, Geolocation, Haptics
 */
import { Capacitor } from '@capacitor/core';

// Check if running as native app
export const isNative = Capacitor.isNativePlatform();

// ========== CAMERA ==========
let Camera;
if (isNative) {
  import('@capacitor/camera').then(mod => { Camera = mod.Camera; });
}

export const takePhoto = async () => {
  if (!isNative || !Camera) return null;
  try {
    const { Camera: Cam, CameraResultType, CameraSource } = await import('@capacitor/camera');
    const photo = await Cam.getPhoto({
      quality: 80,
      allowEditing: false,
      resultType: CameraResultType.DataUrl,
      source: CameraSource.Prompt, // Let user choose camera or gallery
    });
    return photo.dataUrl;
  } catch (e) {
    console.log('Camera cancelled or error:', e);
    return null;
  }
};

// ========== PUSH NOTIFICATIONS ==========
export const initPushNotifications = async (onNotification) => {
  if (!isNative) return;
  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');

    const permResult = await PushNotifications.requestPermissions();
    if (permResult.receive !== 'granted') {
      console.log('Push permission denied');
      return;
    }

    await PushNotifications.register();

    PushNotifications.addListener('registration', (token) => {
      console.log('Push token:', token.value);
      // Send token to backend for storing
      sendPushTokenToBackend(token.value);
    });

    PushNotifications.addListener('pushNotificationReceived', (notification) => {
      console.log('Push received:', notification);
      if (onNotification) onNotification(notification);
    });

    PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      console.log('Push action:', action);
      if (onNotification) onNotification(action.notification);
    });
  } catch (e) {
    console.log('Push notifications not available:', e);
  }
};

const sendPushTokenToBackend = async (token) => {
  try {
    const authToken = localStorage.getItem('token');
    if (!authToken) return;
    const API = process.env.REACT_APP_BACKEND_URL;
    await fetch(`${API}/api/users/push-token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`
      },
      body: JSON.stringify({ push_token: token })
    });
  } catch (e) {
    console.log('Failed to send push token:', e);
  }
};

// ========== GEOLOCATION ==========
export const getCurrentPosition = async () => {
  if (!isNative) {
    // Fallback to browser geolocation
    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        reject
      );
    });
  }
  try {
    const { Geolocation } = await import('@capacitor/geolocation');
    const pos = await Geolocation.getCurrentPosition({ enableHighAccuracy: true });
    return { lat: pos.coords.latitude, lng: pos.coords.longitude };
  } catch (e) {
    console.log('Geolocation error:', e);
    return null;
  }
};

// ========== HAPTICS ==========
export const vibrate = async () => {
  if (!isNative) return;
  try {
    const { Haptics, ImpactStyle } = await import('@capacitor/haptics');
    await Haptics.impact({ style: ImpactStyle.Medium });
  } catch (e) { /* ignore */ }
};

// ========== SHARE ==========
export const nativeShare = async (title, text, url) => {
  if (!isNative) {
    // Fallback to Web Share API
    if (navigator.share) {
      await navigator.share({ title, text, url });
      return true;
    }
    return false;
  }
  try {
    const { Share } = await import('@capacitor/share');
    await Share.share({ title, text, url, dialogTitle: 'Share via NightBuZz' });
    return true;
  } catch (e) {
    console.log('Share error:', e);
    return false;
  }
};
